import { useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { asRows } from '@shared/api/envelope';
import { key, STALE_TIME } from '@shared/api/query-keys';
import { composeCategoryTree, type WireCategoryRow } from '@features/taxonomy/_shared/tree';

import type {
  CategoryTreePayload,
  ConfigurationPayload,
  Shelf,
  ShelfListPayload,
  ShelfParamKind,
  ShelfSurface,
  Tag,
  TagListPayload,
  TagStripPayload,
  TitleListPayload,
  WireShelf,
} from './types';

/**
 * **One read per surface, not one read for the page.**
 *
 * The owning service manages shelves «per `surface`», and every mutation on
 * this page invalidates `['content','shelves',<surface>]` — a key that only
 * means something if the surface is part of it. Two reads also give the two
 * sections independent loading and error states, so the subscriber list stays
 * usable while the public one is retrying, and a panel-level failure never
 * blanks the page.
 */
/**
 * The table row, as the surface reads it.
 *
 * The parameter pair is collapsed into the one nullable object every reader on
 * this surface already expects, and its value is split on the comma the service
 * stores an ordered ULID set in. A `CONTENT_ITEM_SET` whose value is empty keeps
 * its KIND and loses only its members — the shelf is still a hand-picked one
 * that nobody has picked for yet, and dropping the kind would redraw it as a
 * parameterless source the operator never chose.
 */
function toShelf(row: WireShelf): Shelf {
  const values = (row.source_param_value ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  return {
    id: row.id,
    surface: row.surface,
    position: row.position,
    title: row.title,
    layout: row.layout,
    source: row.source,
    sourceParam: row.source_param_kind ? { kind: row.source_param_kind as ShelfParamKind, values } : null,
    seeAllUrl: row.see_all_url,
  };
}

export function shelvesKey(surface: ShelfSurface) {
  return key('content', 'shelves', surface);
}

export function useShelves(surface: ShelfSurface) {
  return useQuery({
    queryKey: shelvesKey(surface),
    queryFn: async (): Promise<ShelfListPayload> => {
      const rows = asRows<WireShelf>(await api.get('content', `/admin/shelves?surface=${surface}`)).map(
        toShelf,
      );
      // The narrowing is re-applied to what arrived. The filter is the service's
      // and it is sent, but the develop edge drops the query string before the
      // request reaches the backend, so both panels are answered with all twelve
      // shelves — and a public shelf drawn in the subscriber section is an
      // operator reordering the wrong surface. Once the edge passes `$args`
      // through this is a no-op over rows the service already narrowed.
      const items = rows.filter((row) => row.surface === surface);
      return { items, page_size: items.length, total: items.length, has_next: false };
    },
    staleTime: STALE_TIME.list,
  });
}

/**
 * The source vocabulary lives in `backend-content`'s own configuration table,
 * because `SHELF_SOURCES` is an admin-tunable **value** rather than a compiled
 * set. Read at the vocabulary cadence: it changes about once a quarter, and a
 * ninth source lands here on the next refetch without a release.
 */
export function useContentConfigurations() {
  return useQuery({
    queryKey: key('content', 'configurations'),
    queryFn: () => api.get<ConfigurationPayload>('content', '/admin/configurations'),
    staleTime: STALE_TIME.vocabulary,
  });
}

/** The genre tree, full depth. The picker flattens it; the tree is not edited here. */
export function useCategories() {
  return useQuery({
    queryKey: key('content', 'categories'),
    // Flat rows on the wire; the picker walks `children`. Same composition the
    // taxonomy surface performs, from the same one place.
    queryFn: async (): Promise<CategoryTreePayload> => ({
      items: composeCategoryTree(asRows<WireCategoryRow>(await api.get('content', '/categories'))),
    }),
    staleTime: STALE_TIME.vocabulary,
  });
}

export function useTags() {
  return useQuery({
    queryKey: key('content', 'tags'),
    queryFn: async (): Promise<TagListPayload> => ({ items: asRows<Tag>(await api.get('content', '/tags')) }),
    staleTime: STALE_TIME.vocabulary,
  });
}

/**
 * The catalogue, as the hand-picked set's vocabulary.
 *
 * **Fetched only while the dialog needs it** — it is the console's largest list
 * and five of the eight sources never open this panel. The picker filters this
 * page client-side with Arabic folding rather than asking the server per
 * keystroke, which is what «searches only what is in its list» means in
 * practice.
 *
 * The scope suffix keeps this out of the content list's own key: that surface
 * reads the same endpoint under its own facets, and one key for two different
 * filter sets would hand each the other's rows.
 */
/** The service's own ceiling on `page_size`; asking for more is a `422`. */
const TITLE_PAGE_CEILING = 100;

export function useTitleOptions(enabled: boolean) {
  return useQuery({
    queryKey: key('content', 'adminItems', 'shelfPicker'),
    // The service's maximum page, not its default. The default of twenty made
    // the picker's client-side search a silent wrong answer — a title outside
    // the first page answered «لا نتيجة تطابق البحث» while it existed — and the
    // dialog said nothing about a limit. The catalogue outgrows even the maximum,
    // so `has_next` and `total` travel to the dialog, which states the reach of
    // the search rather than letting the operator infer it from an empty result.
    queryFn: () =>
      api.get<TitleListPayload>('content', `/admin/contentItems?page_size=${TITLE_PAGE_CEILING}`),
    staleTime: STALE_TIME.vocabulary,
    enabled,
  });
}

export function tagStripKey() {
  return key('content', 'tagStrip');
}

/**
 * The curated chip strip.
 *
 * **Its own key, not the tag vocabulary's.** `useTags` reads every tag — the
 * shelf dialog's `TAG` picker needs the whole set to choose from — while this
 * reads the ordered subset a reader sees. One key for the two would hand each
 * the other's rows, and the two invalidate on different writes.
 *
 * Read at the vocabulary cadence: an operator curates a strip about as often as
 * they add a genre.
 */
export function useTagStrip() {
  return useQuery({
    queryKey: tagStripKey(),
    queryFn: () => api.get<TagStripPayload>('content', '/admin/tagStrip'),
    staleTime: STALE_TIME.vocabulary,
  });
}
